import { RightOutlined } from '@ant-design/icons';
import { colors, radius, shadows } from 'design';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { CategorySummaryDTO } from 'services/api/models/CategoryDTO';
import CategoryIcon from './CategoryIcon';
import { openCategory } from './navigation';

const useStyles = createUseStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface.base,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    cursor: 'pointer',
    '& + &': {
      borderTop: `1px solid ${colors.border.subtle}`,
    },
  },
  icon: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface.calm,
    fontSize: 18,
    color: colors.text.primary,
    overflow: 'hidden',
  },
  text: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  name: {
    margin: 0,
    fontWeight: 500,
  },
  subline: {
    fontSize: '0.85em',
    color: colors.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chevron: {
    color: colors.text.secondary,
    fontSize: '0.8em',
  },
});

/**
 * A list of categories as rows: icon, name and one line naming what is inside,
 * so that someone looking for "mycose" sees it without opening anything.
 */
export default function CategoryRows({
  categories,
  openedFrom,
  getSubline,
  testIdPrefix = 'category',
}: {
  categories: CategorySummaryDTO[];
  openedFrom: string;
  getSubline?: (category: CategorySummaryDTO) => string | undefined;
  testIdPrefix?: string;
}) {
  const styles = useStyles();
  const navigate = useNavigate();

  const subline = (category: CategorySummaryDTO) =>
    getSubline?.(category) ??
    (category.children_names.length > 0 ? category.children_names.join(', ') : category.description);

  return (
    <div className={styles.list}>
      {categories.map((category, index) => {
        const open = () => openCategory(navigate, category.slug, openedFrom);
        return (
          <div
            key={category.id}
            className={styles.row}
            data-testid={`${testIdPrefix}-${index}`}
            onClick={open}
            onKeyDown={e => {
              if (e.key === 'Enter') open();
            }}
            role="button"
            tabIndex={0}
          >
            {(category.icon_url || category.icon) && (
              <span className={styles.icon}>
                <CategoryIcon icon={category.icon} iconUrl={category.icon_url} />
              </span>
            )}
            <span className={styles.text}>
              <h4 className={styles.name}>{category.name}</h4>
              {subline(category) && <span className={styles.subline}>{subline(category)}</span>}
            </span>
            <RightOutlined className={styles.chevron} />
          </div>
        );
      })}
    </div>
  );
}
